import DeckCover from './DeckCover'
import type { DeckCoverSize } from './DeckCover'

/**
 * Shared deck card anatomy (Wave 4).
 *
 * Owns: container geometry and elevation, deck cover slot, title typography
 * (two-line clamp), meta-line style, and the bottom-row action button.
 * Callers fill variant-specific content via explicit slots:
 *   Mine  — coverSlot (checkbox in select mode), trailingSlot (overflow menu),
 *            metaSlot (due count + folder), belowHeaderSlot (progress bar),
 *            bottomLeftSlot (status badge), overlaySlot (delete confirmation).
 *   Catalog — metaSlot (CEFR badge), belowHeaderSlot (description).
 *
 * All tokens are CSS custom properties; no hardcoded colours.
 */

export interface DeckCardProps {
  // ── Identity ──
  deckId: string
  title: string
  cardCount: number
  isSystem?: boolean
  /** System deck topic key for category-icon lookup in DeckCover. */
  category?: string
  /** Cover size token passed to DeckCover. Both Mine and Catalog list cards use 'list' (40 px). Default: 'list'. */
  coverSize?: DeckCoverSize

  // ── Container state ──
  isSelected?: boolean
  /** Enable shadow-lift on hover; Catalog uses it, Mine omits. */
  hoverShadow?: boolean

  // ── Interaction ──
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onPointerDown?: React.PointerEventHandler<HTMLDivElement>
  onPointerMove?: React.PointerEventHandler<HTMLDivElement>
  onPointerUp?: React.PointerEventHandler<HTMLDivElement>
  onPointerCancel?: React.PointerEventHandler<HTMLDivElement>

  // ── Cover-row slots ──
  /** Replaces DeckCover entirely (e.g. select-mode checkbox). */
  coverSlot?: React.ReactNode
  /** Right-aligned trailing control in the cover row (e.g. overflow menu). */
  trailingSlot?: React.ReactNode

  // ── Meta line additions ──
  /** Content appended after "N cards" inside the meta line (dots, badges, …). */
  metaSlot?: React.ReactNode

  // ── Below header ──
  /** Rendered below the cover+text row inside the padded content area.
   *  Caller is responsible for top spacing (e.g. marginTop: 12 on a progress bar). */
  belowHeaderSlot?: React.ReactNode

  // ── Bottom row ──
  /** When undefined the bottom row is not rendered (Mine select mode). */
  actionLabel?: string
  onAction?: React.MouseEventHandler<HTMLButtonElement>
  /** Left-aligned slot in the bottom row (e.g. DeckStatusBadge). */
  bottomLeftSlot?: React.ReactNode

  // ── Absolute overlay ──
  /** Absolutely-positioned overlay rendered at the end of the root div
   *  (e.g. delete-confirmation panel in Mine). */
  overlaySlot?: React.ReactNode
}

export default function DeckCard({
  deckId,
  title,
  cardCount,
  isSystem,
  category,
  coverSize = 'list',
  coverSlot,
  isSelected = false,
  hoverShadow = false,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  trailingSlot,
  metaSlot,
  belowHeaderSlot,
  actionLabel,
  onAction,
  bottomLeftSlot,
  overlaySlot,
}: DeckCardProps) {
  function handleKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault()
      onClick(e as unknown as React.MouseEvent<HTMLDivElement>)
    }
  }

  const baseShadow = isSelected
    ? '0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent)'
    : 'var(--shadow-card)'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={handleKey}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        background: isSelected
          ? 'color-mix(in srgb, var(--accent) 6%, var(--bg-surface))'
          : 'var(--bg-surface)',
        borderRadius: 'var(--radius-md)',
        border: isSelected ? '2px solid var(--accent)' : '1px solid var(--border)',
        boxShadow: baseShadow,
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'manipulation',
        transition: 'border-color 0.12s, box-shadow 0.12s, background 0.12s',
        overflow: 'hidden',
        outline: 'none',
      }}
      onMouseEnter={hoverShadow ? (e) => {
        if (!isSelected) e.currentTarget.style.boxShadow = 'var(--shadow-float)'
      } : undefined}
      onMouseLeave={hoverShadow ? (e) => {
        e.currentTarget.style.boxShadow = baseShadow
      } : undefined}
    >
      {/* ── Main content area ── */}
      {/* Horizontal inset is var(--space-4) — the same value FolderChip's
          own padding uses above this list in LibraryPage — so a card's
          title and a chip's label read from one shared inset rather than
          two components each carrying their own close-but-not-quite value. */}
      <div style={{ padding: `14px var(--space-4) 0 var(--space-4)` }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

          {/* Cover — replaced by coverSlot when provided (select checkbox) */}
          {coverSlot ?? (
            <DeckCover
              deckId={deckId}
              title={title}
              isSystem={isSystem}
              category={category}
              size={coverSize}
            />
          )}

          {/* Text block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                color: 'var(--text-primary)',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {title}
            </h3>

            {/* Meta line: card count + caller additions */}
            <div
              style={{
                fontSize: 13,
                fontFamily: 'var(--font-ui)',
                color: 'var(--text-secondary)',
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <span>{cardCount} card{cardCount !== 1 ? 's' : ''}</span>
              {metaSlot}
            </div>
          </div>

          {/* Trailing control (overflow menu, etc.) */}
          {trailingSlot}
        </div>

        {/* Below header slot: progress bar (Mine) or description (Catalog) */}
        {belowHeaderSlot}
      </div>

      {/* ── Bottom row ── */}
      {actionLabel !== undefined && (
        <div
          style={{ padding: `10px var(--space-4) 12px`, display: 'flex', alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          {bottomLeftSlot}
          <button
            onClick={onAction}
            style={{
              marginLeft: 'auto',
              padding: '7px 20px',
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              minHeight: 34,
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--accent-fill)')}
          >
            {actionLabel}
          </button>
        </div>
      )}

      {/* ── Absolute overlay (delete confirmation, etc.) ── */}
      {overlaySlot}
    </div>
  )
}
